/**
 * How many subjects of a batch to start, and when.
 *
 * Pure, because the rule is the part worth checking and the rest of the batch is
 * entangled with the store: a queue that stalls with slots free wastes an hour
 * of somebody's afternoon, and a queue that over-admits takes the machine into
 * swap, where both runs stop making progress. Neither shows up as an error.
 */

export interface LaunchPlan {
  /** Subject folders to start now, in queue order. */
  start: string[];
  /** What is left waiting afterwards. */
  queue: string[];
}

/**
 * Fill the free slots from the front of the queue.
 *
 * `limit` is the engine's ceiling, decided from the machine's memory; `inFlight`
 * counts runs that have not settled, including ones still `queued` on the Rust
 * side — they hold their place, so counting them is what keeps this from
 * launching a third subject that would sit behind the first two anyway.
 */
export function planLaunch(queue: string[], inFlight: number, limit: number): LaunchPlan {
  const room = Math.max(0, Math.max(1, limit) - Math.max(0, inFlight));
  const start = queue.slice(0, room);
  return { start, queue: queue.slice(start.length) };
}

/**
 * Whether a batch is finished: nothing waiting and nothing running.
 *
 * The queue emptying is not the end. With two subjects in flight the last one to
 * be started is not the last to finish, and announcing completion when the queue
 * empties would report a batch as done while a subject was still registering.
 */
export function batchIsOver(queue: string[], inFlight: number): boolean {
  return queue.length === 0 && inFlight === 0;
}
